import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 2) {
      setError("Bitte gib deinen Namen an.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Bitte gib eine gültige E-Mail-Adresse an.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/bookings`, { name: name.trim(), email: email.trim() });
      if (typeof window.fbq === "function") {
        window.fbq("track", "CompleteRegistration");
      }
      setStatus("done");
    } catch {
      setStatus("idle");
      setError("Etwas hat nicht geklappt. Bitte versuche es erneut.");
    }
  };

  return (
    <section id="buchen" data-testid="booking-section" className="bg-espresso text-cream px-6 md:px-14 py-24 md:py-32">
      <div className="max-w-3xl mx-auto text-center">
        <Reveal>
          <p className="text-xs md:text-sm font-medium uppercase tracking-[0.32em] text-gold mb-6">
            Online-Hypnomeditation · LOSLASSEN
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-serif tracking-tight text-3xl md:text-4xl leading-tight">
            Bereit, <span className="italic text-gold">loszulassen?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-8 text-base md:text-lg font-light leading-relaxed text-cream/75 max-w-xl mx-auto">
            Vielleicht ist genau jetzt der Moment, an dem du aufhörst, dir
            vorzunehmen, irgendwann loszulassen – und dir wirklich Zeit dafür schenkst.
            <br />
            <span className="text-gold">Donnerstag, 03.09.2026 · 20:00–21:00 Uhr</span>
          </p>
        </Reveal>

        <Reveal delay={0.24}>
          <AnimatePresence mode="wait">
            {status === "done" ? (
              <motion.div
                key="done"
                data-testid="booking-success"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mt-14 rounded-md border border-gold/40 bg-cream/5 p-10"
              >
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold text-espresso">
                  <Check size={26} strokeWidth={2} />
                </span>
                <h3 className="mt-6 font-serif text-xl md:text-2xl tracking-tight">
                  Danke, {name.split(" ")[0]} – dein Platz ist reserviert.
                </h3>
                <p className="mt-4 text-sm md:text-base font-light text-cream/70">
                  Du erhältst in Kürze eine E-Mail an <span className="text-gold">{email}</span> mit
                  allen Details zur Zahlung und deinem Zoom-Link.
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                data-testid="booking-form"
                onSubmit={handleSubmit}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5 }}
                className="mt-14 mx-auto max-w-lg text-left"
              >
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.24em] text-cream/60">Dein Name</span>
                  <input
                    data-testid="booking-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Vor- und Nachname"
                    autoComplete="name"
                    className="mt-3 w-full border-b border-cream/30 bg-transparent pb-3 text-base font-light text-cream placeholder:text-cream/35 outline-none transition-colors duration-300 focus:border-gold"
                  />
                </label>
                <label className="mt-9 block">
                  <span className="text-xs uppercase tracking-[0.24em] text-cream/60">Deine E-Mail</span>
                  <input
                    data-testid="booking-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@beispiel.ch"
                    autoComplete="email"
                    className="mt-3 w-full border-b border-cream/30 bg-transparent pb-3 text-base font-light text-cream placeholder:text-cream/35 outline-none transition-colors duration-300 focus:border-gold"
                  />
                </label>

                {error && (
                  <p data-testid="booking-error" className="mt-5 text-sm text-gold">{error}</p>
                )}

                <button
                  data-testid="booking-submit-button"
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-12 flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm md:text-base font-medium tracking-wide text-espresso transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Wird gesendet …
                    </>
                  ) : (
                    "JA, ICH BIN BEREIT LOSZULASSEN · CHF 21.–"
                  )}
                </button>
                <p className="mt-5 text-center text-xs font-light text-cream/50">
                  60 Minuten nur für dich · live via Zoom · bequem von zu Hause
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </Reveal>
      </div>

      <div data-testid="website-link-block" className="mt-20 text-center">
        <p className="text-sm font-light text-cream/60">
          Du möchtest mehr über mich und meine Arbeit erfahren?
        </p>
        <p className="mt-2 text-sm font-light text-cream/60">
          Lerne mich und meine Angebote auf meiner Website kennen.
        </p>
        <a
          data-testid="website-link"
          href="https://www.einfach-gluecklich.ch/"
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-5 inline-flex items-center gap-2 border-b border-cream/30 pb-1 text-sm font-light tracking-wide text-cream/80 transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          Mehr über einfach glücklich
          <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </a>
      </div>

      <footer data-testid="page-footer" className="mt-20 border-t border-cream/15 pt-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-light text-cream/50">
          <span className="font-serif italic text-base text-cream/70">einfach glücklich</span>
          <span>Online-Hypnomeditation LOSLASSEN · 03.09.2026 · Live via Zoom · CHF 21.–</span>
          <span>© 2026 einfach glücklich</span>
        </div>
      </footer>
    </section>
  );
}
